import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";

interface WhitelabelNotification {
  id: string;
  title: string;
  message: string;
  notificationType: string;
  priority: string;
  publishAt: string | null;
  expiresAt: string | null;
  notificationData: any;
}

interface NotificationEditorDialogProps {
  open: boolean;
  onClose: () => void;
  notification: WhitelabelNotification | null;
  whitelabelId: string;
  getToken: () => Promise<string>;
  onSave: () => void;
}

const NOTIFICATION_TYPES = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "success", label: "Success" },
  { value: "announcement", label: "Announcement" },
  { value: "feature", label: "New Feature" },
];

const PRIORITY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
];

export default function NotificationEditorDialog({
  open,
  onClose,
  notification,
  whitelabelId,
  getToken,
  onSave,
}: NotificationEditorDialogProps) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [notificationType, setNotificationType] = useState("info");
  const [priority, setPriority] = useState("normal");
  const [publishAt, setPublishAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!notification;

  useEffect(() => {
    if (notification) {
      setTitle(notification.title);
      setMessage(notification.message);
      setNotificationType(notification.notificationType || "info");
      setPriority(notification.priority || "normal");
      setPublishAt(
        notification.publishAt
          ? new Date(notification.publishAt).toISOString().slice(0, 16)
          : ""
      );
      setExpiresAt(
        notification.expiresAt
          ? new Date(notification.expiresAt).toISOString().slice(0, 16)
          : ""
      );
      setActionUrl(notification.notificationData?.actionUrl || "");
      setActionLabel(notification.notificationData?.actionLabel || "");
    } else {
      // Reset form for new notification
      setTitle("");
      setMessage("");
      setNotificationType("info");
      setPriority("normal");
      setPublishAt("");
      setExpiresAt("");
      setActionUrl("");
      setActionLabel("");
    }
    setError(null);
  }, [notification, open]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!message.trim()) {
      setError("Message is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();

      const notificationData: any = {};
      if (actionUrl) notificationData.actionUrl = actionUrl;
      if (actionLabel) notificationData.actionLabel = actionLabel;

      const payload = {
        title: title.trim(),
        message: message.trim(),
        notificationType,
        priority,
        publishAt: publishAt ? new Date(publishAt).toISOString() : null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        notificationData:
          Object.keys(notificationData).length > 0 ? notificationData : null,
      };

      if (isEditing && notification) {
        await axios.put(
          `${hostname}/whitelabel/${whitelabelId}/notifications/${notification.id}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await axios.post(
          `${hostname}/whitelabel/${whitelabelId}/notifications`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          `Failed to ${isEditing ? "update" : "create"} notification`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isEditing ? "Edit Notification" : "Create Notification"}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            error={!!error && !title.trim()}
            helperText="The notification title shown to users"
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
            error={!!error && !message.trim()}
            helperText="The notification message content. Supports markdown formatting."
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={notificationType}
                onChange={(e) => setNotificationType(e.target.value)}
                label="Type"
              >
                {NOTIFICATION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                label="Priority"
              >
                {PRIORITY_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Publish At"
              type="datetime-local"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to publish immediately"
            />

            <TextField
              label="Expires At"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to never expire"
            />
          </Box>

          <TextField
            label="Action URL (optional)"
            value={actionUrl}
            onChange={(e) => setActionUrl(e.target.value)}
            fullWidth
            placeholder="https://..."
            helperText="Optional link for a 'Learn More' button"
          />

          <TextField
            label="Action Label (optional)"
            value={actionLabel}
            onChange={(e) => setActionLabel(e.target.value)}
            fullWidth
            placeholder="Learn More"
            helperText="Custom label for the action button (default: 'Learn More')"
          />

          {error && (
            <Box sx={{ color: "error.main", mt: 1 }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <LoadingButton onClick={onClose} disabled={saving}>
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleSave}
          loading={saving}
          variant="contained"
          color="primary"
        >
          {isEditing ? "Update" : "Create"} Notification
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
